import React, { useState } from 'react';
import type { StToggleItem } from '../../growth-settings/types';

export const stLabelClass = 'text-[13.5px] font-semibold text-[var(--ink-1)]';
export const stHintClass = 'text-[12.5px] leading-[1.5] text-[var(--ink-3)]';

/** Inert presentation toggle (local state only). */
export function StToggle({
  value,
  onChange,
  'aria-label': ariaLabel,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      data-on={value}
      className="gs-toggle"
      onClick={() => onChange(!value)}
    />
  );
}

/** A toggle row (label + description + switch). Controlled when `onEnabledChange` is set. */
export function StToggleRow({
  label,
  description,
  enabled = true,
  onEnabledChange,
}: StToggleItem & { onEnabledChange?: (next: boolean) => void }) {
  const [internal, setInternal] = useState(enabled);
  const on = onEnabledChange ? enabled : internal;
  const setOn = (next: boolean) => {
    onEnabledChange?.(next);
    if (!onEnabledChange) setInternal(next);
  };
  return (
    <div className="flex items-center justify-between gap-3 border-b border-dashed border-[var(--cream-2)] px-6 py-[18px] last:border-b-0">
      <div className="min-w-0">
        <p className={stLabelClass}>{label}</p>
        <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">{description}</p>
      </div>
      <StToggle value={on} onChange={setOn} aria-label={label} />
    </div>
  );
}

/** Footer actions for editable settings sections. */
export interface StSectionSaveFooter {
  onSave: () => void;
  onDiscard: () => void;
  loading?: boolean;
  saveDisabled?: boolean;
  showDiscard?: boolean;
}

/** Card wrapper for one settings section, with section header + optional save footer. */
export function StSectionCard({
  title,
  description,
  children,
  saveFooter,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  saveFooter?: StSectionSaveFooter;
}) {
  return (
    <section className="elva-fade overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
      <header className="border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-[18px]">
        <h2 className="m-0 font-[var(--font-display)] text-[18px] font-semibold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
          {title}
        </h2>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-normal text-[var(--ink-3)]">
          {description}
        </p>
      </header>
      <div className="space-y-6 px-6 py-6">{children}</div>
      {saveFooter ? (
        <div className="flex justify-end gap-3 border-t border-[var(--cream-2)] px-6 py-4">
          {saveFooter.showDiscard ? (
            <button
              type="button"
              onClick={saveFooter.onDiscard}
              disabled={saveFooter.loading}
              className="inline-flex h-9 cursor-pointer items-center rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[13px] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Discard
            </button>
          ) : null}
          <button
            type="button"
            onClick={saveFooter.onSave}
            disabled={saveFooter.saveDisabled || saveFooter.loading}
            className="inline-flex h-9 cursor-pointer items-center rounded-[var(--r-md)] border border-[var(--success)] bg-[var(--success)] px-4 text-[13px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saveFooter.loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** Inset panel surface. */
export function StFlatPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3">
      {children}
    </div>
  );
}

/** A pill (success-tinted) used in chip rows. */
export function StChip({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex h-7 items-center rounded-[var(--r-pill)] border px-3 text-[13px] font-medium text-[var(--success)]"
      style={{
        borderColor: 'color-mix(in srgb, var(--success) 35%, transparent)',
        background: 'var(--success-tint)',
      }}
    >
      {children}
    </span>
  );
}

/** A dashed "+ Add" affordance used after chip rows (inert). */
export function StAddChip({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 cursor-pointer items-center rounded-[var(--r-pill)] border border-dashed border-[var(--ink-4)] bg-[var(--cream-1)] px-3 text-[13px] font-medium text-[var(--ink-3)]"
    >
      {label}
    </button>
  );
}

/** Heading for an inner sub-section inside a settings card. */
export function StFieldHeading({
  title,
  sub,
}: {
  title: string;
  sub?: string;
}) {
  return (
    <div>
      <h3 className="m-0 text-[13.5px] font-semibold text-[var(--ink-1)]">{title}</h3>
      {sub ? <p className="mt-1 text-[13px] leading-normal text-[var(--ink-3)]">{sub}</p> : null}
    </div>
  );
}

/** Display heading (used for the bold sub-sections in Autopilot / Notifications). */
export function StDisplayHeading({
  title,
  sub,
}: {
  title: string;
  sub?: string;
}) {
  return (
    <div>
      <h3 className="m-0 font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-1)]">
        {title}
      </h3>
      {sub ? <p className="mt-1 text-[13px] leading-normal text-[var(--ink-3)]">{sub}</p> : null}
    </div>
  );
}
